import { useState } from "react";
import { motion } from "framer-motion";
import { Send, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MarqueeRow = () => {
  const words = ["LAUNCHING SOON", "जल्द आ रहा है", "KHABRIK"];
  const row = [...words, ...words, ...words, ...words];
  return (
    <div className="overflow-hidden border-y border-white/10 py-4 w-full bg-[#101014]">
      <div className="flex whitespace-nowrap animate-marquee-slow">
        {row.map((w, i) => (
          <span key={i} className="mx-6 flex items-center gap-6">
            <span className={`font-display text-2xl sm:text-3xl ${i % 2 === 0 ? "text-[#FFD400]" : "text-slate-400"}`}>{w}</span>
            <span className="w-2 h-2 rounded-full bg-[#FFD400]/40" />
          </span>
        ))}
      </div>
    </div>
  );
};

export default function LaunchingSoon() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("कृपया सही ईमेल दर्ज करें");
      return;
    }
    setBusy(true);
    try {
      await subscribeNewsletter(email);
      setDone(true);
      toast.success("धन्यवाद! लॉन्च की सूचना आपको सबसे पहले मिलेगी।");
    } catch {
      toast.error("कुछ गलत हो गया, पुनः प्रयास करें");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-6.5rem)] flex flex-col items-center justify-center text-center px-4" data-testid="launching-soon">
      <motion.img
        src="/khabrik-logo.png"
        alt="Khabrik"
        className="w-24 h-24 sm:w-32 sm:h-32 rounded-full"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      />
      <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl text-white mt-10 leading-[1.25]">
        <span className="block overflow-hidden pt-3 pb-2">
          <motion.span className="block" custom={0} variants={lineReveal} initial="hidden" animate="show">
            <span className="text-white font-[Outfit] font-extrabold tracking-tight">Khabrik</span>
          </motion.span>
        </span>
        <span className="block overflow-hidden pt-2 pb-3">
          <motion.span className="block text-3xl sm:text-5xl lg:text-6xl text-slate-300" custom={1} variants={lineReveal} initial="hidden" animate="show">
            जल्द आ रहा है
          </motion.span>
        </span>
      </h1>
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.6 }}
        className="text-slate-400 text-base sm:text-lg mt-6 max-w-md leading-relaxed"
      >
        सिर्फ़ सुर्खियाँ नहीं — हर खबर की पूरी कहानी। विस्तृत, निष्पक्ष और गहराई से की गई पत्रकारिता, बहुत जल्द आपके सामने।
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.6 }}
        className="mt-10 w-full max-w-md"
      >
        {done ? (
          <p className="flex items-center justify-center gap-2 text-[#FFD400] font-semibold" data-testid="notify-success">
            <MailCheck size={18} /> धन्यवाद! लॉन्च होते ही आपको सूचना मिलेगी।
          </p>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="notify-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="लॉन्च की सूचना पाएं — ईमेल दर्ज करें"
              className="flex-1 bg-[#15151A] border border-white/15 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#FFD400] transition-colors placeholder:text-slate-500"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="notify-subscribe-button"
              className="flex items-center justify-center gap-2 bg-[#FFD400] text-[#0D0D10] px-6 py-3 text-sm font-bold hover:bg-[#E6BE00] transition-colors disabled:opacity-60"
            >
              <Send size={14} /> {busy ? "..." : "सूचित करें"}
            </button>
          </form>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="mt-14 mb-10 w-full"
      >
        <MarqueeRow />
      </motion.div>
    </div>
  );
}
